/**
 * CrmHealthTile — top-of-page status tile for the SuperAdmin → Operations → CRM subtab.
 *
 * Renders CRM connection health at a glance (colored dot + text), the environment
 * context the current backend is talking to (API host, name suffix, enabled flag),
 * when the last health probe ran, and how stale the cached probe value is. Includes
 * a manual "Refresh" button that bypasses the 60 s health cache on the server.
 *
 * The tile derives three visual states from the `health` prop:
 *   - green  → health.ok === true                                (Reachable)
 *   - gray   → health.ok === false && health.detail === 'disabled' (Disabled)
 *   - red    → health.ok === false && health.detail !== 'disabled' (Unreachable)
 *
 * @param {Object}   props
 * @param {Object}   props.health       - {ok:boolean, detail:string} from GET /admin/crm/health
 * @param {Object}   [props.envInfo]    - {apiUrl:string, nameSuffix:string, enabled:boolean}
 * @param {string}   [props.lastProbe]  - ISO timestamp of the last probe (server-side)
 * @param {number}   [props.cacheAgeSec] - Age of the cached probe value in seconds
 * @param {Function} props.onRefresh    - Handler wired to the Refresh button
 * @param {boolean}  [props.loading]    - Disables the Refresh button when true
 * @returns {JSX.Element}
 */
import React from 'react';

/**
 * Format an ISO timestamp as a localized time string, falling back gracefully.
 * @param {string} iso - ISO 8601 timestamp
 * @returns {string} Localized "time — date" string, or "—" when input is missing/invalid
 */
function formatLocalTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleTimeString()} · ${d.toLocaleDateString()}`;
}

/**
 * Extract just the host portion of an API URL for compact display.
 * @param {string} url - Full URL string
 * @returns {string} host (e.g. "twenty.example.com") or the raw input when unparseable
 */
function hostOf(url) {
  if (!url) return '—';
  try {
    return new URL(url).host;
  } catch (_e) {
    return url;
  }
}

function CrmHealthTile({ health, envInfo, lastProbe, cacheAgeSec, onRefresh, loading }) {
  const ok = health && health.ok === true;
  const detail = (health && health.detail) || '';
  const disabled = !ok && detail === 'disabled';

  // Status classification: 'ok' → green, 'disabled' → gray, otherwise 'error' → red.
  let statusKey;
  let statusText;
  if (ok) {
    statusKey = 'ok';
    statusText = 'Reachable';
  } else if (disabled) {
    statusKey = 'disabled';
    statusText = 'Disabled';
  } else {
    statusKey = 'error';
    statusText = 'Unreachable';
  }

  const isStale = typeof cacheAgeSec === 'number' && cacheAgeSec > 60;
  const apiHost = hostOf(envInfo && envInfo.apiUrl);
  const nameSuffix = (envInfo && envInfo.nameSuffix) || '';
  const enabledFlag = envInfo && typeof envInfo.enabled === 'boolean'
    ? (envInfo.enabled ? 'true' : 'false')
    : '—';

  return (
    <div
      className="card crm-health-tile"
      role="status"
      aria-live="polite"
      data-testid="crm-health-tile"
    >
      <div className="crm-health-tile__header">
        <div className="crm-health-tile__status">
          <span
            className={`crm-health-tile__dot crm-health-tile__dot--${statusKey}`}
            aria-hidden="true"
          />
          <span
            className="crm-health-tile__status-text"
            data-testid="crm-health-status"
          >
            {statusText}
          </span>
          {detail && !disabled && !ok ? (
            <span className="crm-health-tile__detail">({detail})</span>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onRefresh}
          disabled={!!loading}
          aria-label="Refresh CRM health probe (bypasses cache)"
          data-testid="crm-health-refresh"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <dl className="crm-health-tile__meta">
        <div className="crm-health-tile__meta-row">
          <dt>API host</dt>
          <dd className="crm-health-tile__mono">{apiHost}</dd>
        </div>
        <div className="crm-health-tile__meta-row">
          <dt>Name suffix</dt>
          <dd className="crm-health-tile__mono">{nameSuffix || '—'}</dd>
        </div>
        <div className="crm-health-tile__meta-row">
          <dt>CRM_ENABLED</dt>
          <dd className="crm-health-tile__mono">{enabledFlag}</dd>
        </div>
      </dl>

      <div className="crm-health-tile__footer">
        <span className="crm-health-tile__last-probe">
          Last probe:{' '}
          <span className="crm-health-tile__mono">{formatLocalTime(lastProbe)}</span>
        </span>
        {typeof cacheAgeSec === 'number' ? (
          <span
            className={
              isStale
                ? 'crm-health-tile__cache-age crm-health-tile__cache-age--stale'
                : 'crm-health-tile__cache-age'
            }
            data-testid="crm-health-cache-age"
          >
            cached {Math.max(0, Math.floor(cacheAgeSec))}s ago
            {isStale ? (
              <span
                className="crm-health-tile__stale"
                data-testid="crm-health-stale"
              >
                {' '}· stale
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default CrmHealthTile;
